import { useEffect } from "react";

// Externals
import { EditorContent, useEditor } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import { Placeholder } from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";

// Styles
import "./styles.css";

// Interfaces
import { IExtendedTipEditorProps } from "../interfaces";
import clsx from "clsx";

const DisableEnter = Extension.create({
	addKeyboardShortcuts() {
		return {
			Enter: () => true, // NOSONAR
			"Shift-Enter": () => true, // NOSONAR
			"Control-Enter": () => true, // NOSONAR
		};
	},
});

export default function ExtendedTipEditor(props: Readonly<IExtendedTipEditorProps>) {
	const {
		content = "",
		placeholder = "",
		suggestion = "",
		inputProps = undefined,
		disabled = false,
		onBlur = () => {},
		onFocus = () => {},
		onKeyDown = () => {},
		onPreventedEvent = () => {},
		onUpdate = () => {},
		onTextInput = () => false,
	} = props;

	const { inputClasses = "", placeholderClasses = "" } = inputProps || {};

	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				bulletList: {
					keepMarks: true,
					keepAttributes: false,
				},
				orderedList: {
					keepMarks: true,
					keepAttributes: false,
				},
			}),
			Placeholder.configure({
				placeholder,
				showOnlyWhenEditable: false,
				emptyNodeClass: clsx({
					"predictive-input-placeholder": true,
					[placeholderClasses]: Boolean(placeholderClasses),
				}),
			}),
			DisableEnter,
		],
		enableInputRules: [StarterKit, DisableEnter, Placeholder],
		enablePasteRules: [StarterKit, DisableEnter, Placeholder],
		editorProps: {
			attributes: {
				class: clsx({
					"focus:outline-none": true,
					"!cursor-default": disabled,
					[inputClasses]: Boolean(inputClasses),
				}),
				spellcheck: "false",
				autocomplete: "off",
				autocapitalize: "off",
				"data-suggestion": suggestion,
				"data-suggestion-display": !suggestion ? "none" : "inherit",
			},
			handleKeyDown: (_, event) => {
				if (event.key === "Enter" || event.key === "Tab") {
					onPreventedEvent(event);
					event.preventDefault();
					event.stopPropagation();
				}
				onKeyDown(event);
			},
			handleTextInput: onTextInput,
		},
		content,
		onUpdate,
		onFocus,
		onBlur,
	});

	useEffect(() => {
		if (editor) {
			editor.setEditable(!disabled);
			if (props.editorRef) {
				props.editorRef.current = editor;
			}
		}
	}, [editor, disabled]);

	return <EditorContent editor={editor} />;
}
